import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BookOpenCheck,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock3,
  Database,
  DollarSign,
  Eye,
  FileCheck2,
  FileSearch,
  Filter,
  History,
  Layers3,
  ListChecks,
  Gauge,
  Loader2,
  MessageSquareMore,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { requirementsAPI } from "../../../services/requirementsAPI";
import { isSystemAdmin } from "../../../constants/systemRoles";

const SOURCE_TYPES = [
  ["specification", "Specifications"],
  ["rfi", "RFIs"],
  ["drawing", "Drawings"],
  ["code_standard", "Codes / Standards"],
  ["submittal", "Submittals"],
  ["itp", "ITPs"],
  ["method_statement", "Method Statements"],
  ["manufacturer_document", "Manufacturer Documents"],
];

const QUICK_REFINEMENTS = [
  "Focus only on installation requirements.",
  "Include more approved RFIs, drawings, and submittals.",
  "Remove product information that is not field-inspectable.",
  "Give more detail while keeping every statement source-grounded.",
];

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return error?.message || fallback;
};

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number(value || 0) < 0.01 ? 4 : 2,
    maximumFractionDigits: Number(value || 0) < 0.01 ? 6 : 2,
  }).format(Number(value || 0));

const BudgetBar = ({ label, spend, limit }) => {
  const enabled = Number(limit || 0) > 0;
  const ratio = enabled ? Math.min(1, Number(spend || 0) / Number(limit)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs font-bold text-slate-600">
        <span>{label}</span>
        <span>
          {enabled
            ? `${money(spend)} / ${money(limit)}`
            : "No limit configured"}
        </span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full ${ratio >= 0.9 ? "bg-rose-500" : ratio >= 0.75 ? "bg-amber-500" : "bg-emerald-500"}`}
          style={{ width: `${Math.max(enabled ? 2 : 0, ratio * 100)}%` }}
        />
      </div>
    </div>
  );
};

const StatusBadge = ({ value }) => {
  const status = String(value || "draft").toLowerCase();
  const tones = {
    approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    draft: "bg-amber-50 text-amber-700 ring-amber-200",
    rejected: "bg-rose-50 text-rose-700 ring-rose-200",
    stale: "bg-orange-50 text-orange-700 ring-orange-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset ${
        tones[status] || "bg-slate-50 text-slate-600 ring-slate-200"
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {titleCase(status)}
    </span>
  );
};

const Metric = ({ icon: Icon, label, value, helper }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between text-slate-500">
      <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
      <Icon className="h-4 w-4" />
    </div>
    <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
    <div className="mt-1 text-xs text-slate-500">{helper}</div>
  </div>
);

const RequirementIntelligenceView = () => {
  const { activeProject, currentUser, projectRole } = useProjectContext();
  const projectId = activeProject?.id;
  const admin = isSystemAdmin(currentUser?.role);
  const role = String(projectRole || "").toLowerCase();
  const canQuery =
    admin || ["project_manager", "qa_qc_manager", "inspector"].includes(role);
  const canApprove =
    admin || ["project_manager", "qa_qc_manager", "inspector"].includes(role);
  const canGenerate =
    admin || ["project_manager", "qa_qc_manager", "inspector"].includes(role);

  const [step, setStep] = useState("ask");
  const [question, setQuestion] = useState("");
  const [location, setLocation] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [sourceTypes, setSourceTypes] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [queryMeta, setQueryMeta] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [relatedAnswers, setRelatedAnswers] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);

  const [refineOpen, setRefineOpen] = useState(false);
  const [refinement, setRefinement] = useState("");
  const [checklistScope, setChecklistScope] = useState({
    inspection_subject: "",
    location: "",
    inspection_stage: "",
    instructions: "",
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const loadUsage = useCallback(async () => {
    if (!admin || !projectId) return;
    setUsageLoading(true);
    try {
      const response = await requirementsAPI.adminUsage({
        project_id: projectId,
        days: 30,
      });
      setUsage(response.data || null);
    } catch (usageError) {
      setError(getError(usageError, "Could not load AI cost information."));
    } finally {
      setUsageLoading(false);
    }
  }, [admin, projectId]);

  const loadAnswers = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await requirementsAPI.listAnswers(projectId, {
        limit: 60,
      });
      setAnswers(response.data || []);
    } catch (loadError) {
      setError(getError(loadError, "Could not load saved answers."));
    }
  }, [projectId]);

  useEffect(() => {
    loadAnswers();
  }, [loadAnswers]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const answerSources = currentAnswer?.sources || [];
  const sourceById = useMemo(
    () =>
      Object.fromEntries(
        answerSources.map((source) => [source.source_id, source]),
      ),
    [answerSources],
  );
  const sourceFamilies = useMemo(
    () =>
      Array.from(
        new Set(
          answerSources
            .map((source) => source?.metadata?.document_type)
            .filter(Boolean),
        ),
      ),
    [answerSources],
  );

  const openAnswer = async (answerId) => {
    setBusy(`open-${answerId}`);
    setError("");
    try {
      const response = await requirementsAPI.getAnswer(projectId, answerId);
      setCurrentAnswer(response.data);
      setQueryMeta(null);
      setRelatedAnswers([]);
      setQuestion(response.data.question || "");
      setLocation(response.data.location || "");
      setStep(response.data.status === "approved" ? "review" : "review");
      setPreview(null);
      setChecklistScope((current) => ({
        ...current,
        inspection_subject: response.data.question || "",
        location: response.data.location || "",
      }));
    } catch (openError) {
      setError(getError(openError, "Could not open the saved answer."));
    } finally {
      setBusy("");
    }
  };

  const submitQuestion = async (forceRefresh = false) => {
    if (!question.trim()) return;
    setBusy("query");
    setError("");
    setPreview(null);
    try {
      const response = await requirementsAPI.queryAnswer(projectId, {
        question: question.trim(),
        location: location.trim() || null,
        discipline: discipline || null,
        source_types: sourceTypes,
        force_refresh: forceRefresh,
      });
      setCurrentAnswer(response.data.answer);
      setQueryMeta(response.data);
      setRelatedAnswers(response.data.related_answers || []);
      setChecklistScope({
        inspection_subject: question.trim(),
        location: location.trim(),
        inspection_stage: "",
        instructions: "",
      });
      setStep("review");
      await Promise.all([loadAnswers(), loadUsage()]);
    } catch (queryError) {
      setError(getError(queryError, "Project-document search failed."));
    } finally {
      setBusy("");
    }
  };

  const refineAnswer = async () => {
    if (!currentAnswer?.id || !refinement.trim()) return;
    setBusy("refine");
    setError("");
    try {
      const response = await requirementsAPI.refineAnswer(
        projectId,
        currentAnswer.id,
        refinement.trim(),
      );
      setCurrentAnswer(response.data.answer);
      setQueryMeta(response.data);
      setRelatedAnswers(response.data.related_answers || []);
      setRefinement("");
      setRefineOpen(false);
      setPreview(null);
      await Promise.all([loadAnswers(), loadUsage()]);
    } catch (refineError) {
      setError(getError(refineError, "Answer refinement failed."));
    } finally {
      setBusy("");
    }
  };

  const approveAnswer = async () => {
    if (!currentAnswer?.id) return;
    setBusy("approve");
    setError("");
    try {
      const response = await requirementsAPI.approveAnswer(
        projectId,
        currentAnswer.id,
      );
      setCurrentAnswer(response.data);
      setChecklistScope((current) => ({
        ...current,
        inspection_subject:
          current.inspection_subject || response.data.question,
        location: current.location || response.data.location || "",
      }));
      await loadAnswers();
    } catch (approveError) {
      setError(getError(approveError, "Answer approval failed."));
    } finally {
      setBusy("");
    }
  };

  const rejectAnswer = async () => {
    if (!currentAnswer?.id) return;
    const note = window.prompt("Why is this answer being rejected?", "");
    if (note === null) return;
    setBusy("reject");
    setError("");
    try {
      const response = await requirementsAPI.rejectAnswer(
        projectId,
        currentAnswer.id,
        note.trim() || null,
      );
      setCurrentAnswer(response.data);
      await loadAnswers();
    } catch (rejectError) {
      setError(getError(rejectError, "Answer rejection failed."));
    } finally {
      setBusy("");
    }
  };

  const refreshAnswer = async () => {
    if (!currentAnswer?.id) return;
    setBusy("refresh");
    setError("");
    try {
      const response = await requirementsAPI.refreshAnswer(
        projectId,
        currentAnswer.id,
      );
      setCurrentAnswer(response.data.answer);
      setQueryMeta(response.data);
      setRelatedAnswers(response.data.related_answers || []);
      setPreview(null);
      await Promise.all([loadAnswers(), loadUsage()]);
    } catch (refreshError) {
      setError(getError(refreshError, "Answer refresh failed."));
    } finally {
      setBusy("");
    }
  };

  const generateChecklist = async () => {
    if (!currentAnswer?.id) return;
    setBusy("checklist");
    setError("");
    try {
      const response = await requirementsAPI.checklistPreviewFromAnswer(
        projectId,
        currentAnswer.id,
        {
          inspection_subject: checklistScope.inspection_subject.trim() || null,
          location: checklistScope.location.trim() || null,
          inspection_stage: checklistScope.inspection_stage.trim() || null,
          instructions: checklistScope.instructions.trim() || null,
        },
      );
      setPreview({
        ...response.data,
        items: (response.data.items || []).map((item) => ({
          ...item,
          enabled: true,
        })),
      });
      setStep("checklist");
      await loadUsage();
    } catch (checklistError) {
      setError(getError(checklistError, "Checklist generation failed."));
    } finally {
      setBusy("");
    }
  };

  const updatePreviewItem = (index, patch) => {
    setPreview((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    }));
  };

  const saveChecklist = async () => {
    if (!preview || !currentAnswer?.id) return;
    const items = preview.items
      .filter((item) => item.enabled !== false)
      .map(({ enabled, ...item }) => item);
    if (!items.length) {
      setError("Keep at least one checklist item before saving.");
      return;
    }
    setBusy("save-checklist");
    setError("");
    try {
      await requirementsAPI.saveChecklistFromAnswer(
        projectId,
        currentAnswer.id,
        {
          ...preview,
          items,
        },
      );
      window.alert("Source-grounded inspection template saved.");
      setPreview(null);
      setStep("review");
      await loadUsage();
    } catch (saveError) {
      setError(getError(saveError, "Checklist save failed."));
    } finally {
      setBusy("");
    }
  };

  const startNewQuestion = () => {
    setCurrentAnswer(null);
    setQueryMeta(null);
    setRelatedAnswers([]);
    setPreview(null);
    setRefineOpen(false);
    setRefinement("");
    setQuestion("");
    setLocation("");
    setStep("ask");
  };

  const renderNarrativeLine = (line, index) => {
    const clean = line.trim();
    if (!clean) return <div key={`gap-${index}`} className="h-3" />;
    const heading = clean.match(/^(#{1,4})\s+(.*)$/);
    const bullet = clean.match(/^[-*]\s+(.*)$/);
    const text = heading ? heading[2] : bullet ? bullet[1] : clean;
    const parts = text.split(/(\[S\d+\])/g);
    const body = parts.map((part, partIndex) => {
      const match = part.match(/^\[(S\d+)\]$/);
      if (!match)
        return <React.Fragment key={partIndex}>{part}</React.Fragment>;
      const source = sourceById[match[1]];
      return (
        <button
          type="button"
          key={partIndex}
          onClick={() => source && setSelectedSource(source)}
          className="mx-0.5 inline-flex rounded-md bg-indigo-50 px-1.5 py-0.5 text-xs font-black text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100"
        >
          {part}
        </button>
      );
    });
    if (heading) {
      return (
        <h4
          key={index}
          className="mt-5 text-base font-black text-slate-900 first:mt-0"
        >
          {body}
        </h4>
      );
    }
    if (bullet) {
      return (
        <div key={index} className="flex gap-3 py-1.5 leading-7 text-slate-700">
          <CheckCircle2 className="mt-1.5 h-4 w-4 shrink-0 text-emerald-600" />
          <div>{body}</div>
        </div>
      );
    }
    return (
      <p key={index} className="py-1.5 leading-7 text-slate-700">
        {body}
      </p>
    );
  };

  if (!projectId) return null;

  const steps = [
    { id: "ask", label: "Ask Project Documents", icon: Search },
    { id: "review", label: "Review & Approve", icon: BookOpenCheck },
    { id: "checklist", label: "Generate Checklist", icon: ListChecks },
  ];

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-3xl bg-slate-950 text-white shadow-xl">
        <div className="relative p-6 md:p-8">
          <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-orange-500/20 blur-3xl" />
          <div className="absolute -bottom-28 left-1/3 h-56 w-56 rounded-full bg-indigo-500/20 blur-3xl" />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold ring-1 ring-white/15">
                <ShieldCheck className="h-4 w-4 text-orange-300" />
                Source-grounded Requirement Intelligence
              </div>
              <h2 className="mt-4 text-2xl font-black tracking-tight md:text-3xl">
                Ask once. Verify the evidence. Build the checklist.
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
                Searches approved/current specifications, RFIs, drawings, codes,
                submittals, and other indexed project authorities with hybrid
                retrieval. The answer may use only the returned source chunks.
              </p>
            </div>
            <button
              type="button"
              onClick={loadAnswers}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-bold ring-1 ring-white/15 hover:bg-white/15"
            >
              <RefreshCw className="h-4 w-4" /> Refresh saved answers
            </button>
          </div>
        </div>
      </section>

      {admin && (
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-100 text-emerald-700">
                <DollarSign className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-black text-slate-900">
                  AI cost and limits
                </h3>
                <p className="text-sm text-slate-500">
                  Current project · model calls and Knowledge Base retrieval
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={loadUsage}
              disabled={usageLoading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {usageLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh cost
            </button>
          </div>

          {usage && (
            <>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <Metric
                  icon={Clock3}
                  label="Today"
                  value={money(usage.today?.estimated_cost)}
                  helper={`${usage.today?.calls || 0} requests`}
                />
                <Metric
                  icon={DollarSign}
                  label="Month to date"
                  value={money(usage.month_to_date?.estimated_cost)}
                  helper={`${usage.month_to_date?.calls || 0} requests`}
                />
                <Metric
                  icon={Gauge}
                  label="Last 30 days"
                  value={money(usage.selected_period?.estimated_cost)}
                  helper={`${usage.selected_period?.input_tokens || 0} input tokens`}
                />
                <Metric
                  icon={AlertTriangle}
                  label="Unpriced calls"
                  value={usage.unpriced_calls || 0}
                  helper="Must be zero for reliable limits"
                />
              </div>

              <div className="mt-5 grid gap-5 lg:grid-cols-2">
                <div className="space-y-4 rounded-2xl bg-slate-50 p-4">
                  <BudgetBar
                    label="Project daily budget"
                    spend={usage.budgets?.project?.daily_spend}
                    limit={usage.budgets?.project?.daily_limit}
                  />
                  <BudgetBar
                    label="Project monthly budget"
                    spend={usage.budgets?.project?.monthly_spend}
                    limit={usage.budgets?.project?.monthly_limit}
                  />
                </div>
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="text-xs font-black uppercase tracking-wide text-slate-500">
                    Top cost drivers
                  </div>
                  <div className="mt-3 grid gap-4 sm:grid-cols-2">
                    <div>
                      <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                        Models
                      </div>
                      <div className="mt-2 space-y-2">
                        {(usage.by_model || []).slice(0, 4).map((row) => (
                          <div
                            key={row.id}
                            className="flex items-center justify-between gap-3 text-sm"
                          >
                            <span
                              className="truncate font-semibold text-slate-700"
                              title={row.name}
                            >
                              {row.name}
                            </span>
                            <span className="shrink-0 font-black text-slate-900">
                              {money(row.estimated_cost)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] font-black uppercase tracking-wide text-slate-400">
                        Users
                      </div>
                      <div className="mt-2 space-y-2">
                        {(usage.by_user || []).slice(0, 4).map((row) => (
                          <div
                            key={row.id}
                            className="flex items-center justify-between gap-3 text-sm"
                          >
                            <span
                              className="truncate font-semibold text-slate-700"
                              title={row.name}
                            >
                              {row.name}
                            </span>
                            <span className="shrink-0 font-black text-slate-900">
                              {money(row.estimated_cost)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {(usage.by_model || []).length === 0 && (
                    <div className="mt-3 text-sm text-slate-500">
                      No AI usage recorded for this project.
                    </div>
                  )}
                </div>
              </div>

              {(usage.pricing?.models || []).some((row) => !row.configured) && (
                <div className="mt-4 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Pricing is missing for one or more configured models. Add
                    every active model to AI_MODEL_PRICING_JSON before relying
                    on budget enforcement.
                  </span>
                </div>
              )}
            </>
          )}
        </section>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        {steps.map((item, index) => {
          const Icon = item.icon;
          const active = step === item.id;
          const complete =
            (index === 0 && currentAnswer) ||
            (index === 1 && currentAnswer?.status === "approved");
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                if (item.id === "ask") setStep("ask");
                if (item.id === "review" && currentAnswer) setStep("review");
                if (
                  item.id === "checklist" &&
                  currentAnswer?.status === "approved"
                )
                  setStep("checklist");
              }}
              className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition ${
                active
                  ? "border-orange-300 bg-orange-50 shadow-sm"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div
                className={`grid h-10 w-10 place-items-center rounded-xl ${
                  complete
                    ? "bg-emerald-100 text-emerald-700"
                    : active
                      ? "bg-orange-500 text-white"
                      : "bg-slate-100 text-slate-500"
                }`}
              >
                {complete ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <Icon className="h-5 w-5" />
                )}
              </div>
              <div>
                <div className="text-xs font-bold text-slate-400">
                  STEP {index + 1}
                </div>
                <div className="font-black text-slate-900">{item.label}</div>
              </div>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>{error}</div>
          <button
            type="button"
            onClick={() => setError("")}
            className="ml-auto"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {step === "ask" && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-7">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-100 text-orange-700">
                <FileSearch className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900">
                  Ask the approved project documents
                </h3>
                <p className="text-sm text-slate-500">
                  Use an equipment, system, activity, stage, or exact section
                  reference.
                </p>
              </div>
            </div>

            <label className="mt-6 block text-xs font-black uppercase tracking-wide text-slate-500">
              Question
            </label>
            <textarea
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              rows={5}
              placeholder="What are the installation requirements for panelboards?"
              className="mt-2 w-full rounded-2xl border border-slate-300 bg-slate-50 px-4 py-3 text-base text-slate-900 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100"
            />

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-xs font-black uppercase tracking-wide text-slate-500">
                  Location / area — optional
                </label>
                <input
                  value={location}
                  onChange={(event) => setLocation(event.target.value)}
                  placeholder="Building A · Level 03"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                />
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-wide text-slate-500">
                  Discipline — optional
                </label>
                <input
                  value={discipline}
                  onChange={(event) => setDiscipline(event.target.value)}
                  placeholder="Electrical"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setAdvancedOpen((value) => !value)}
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900"
            >
              <Filter className="h-4 w-4" /> Source filters
              <ChevronDown
                className={`h-4 w-4 transition ${advancedOpen ? "rotate-180" : ""}`}
              />
            </button>

            {advancedOpen && (
              <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-bold text-slate-500">
                  Leave all unchecked to search every approved/current source
                  type.
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {SOURCE_TYPES.map(([value, label]) => {
                    const selected = sourceTypes.includes(value);
                    return (
                      <button
                        type="button"
                        key={value}
                        onClick={() =>
                          setSourceTypes((current) =>
                            selected
                              ? current.filter((item) => item !== value)
                              : [...current, value],
                          )
                        }
                        className={`rounded-full px-3 py-1.5 text-xs font-bold ring-1 transition ${
                          selected
                            ? "bg-slate-900 text-white ring-slate-900"
                            : "bg-white text-slate-600 ring-slate-300 hover:ring-slate-400"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => submitQuestion(false)}
                disabled={!canQuery || !question.trim() || busy === "query"}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy === "query" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                {busy === "query"
                  ? "Searching and grounding…"
                  : "Search Project Documents"}
              </button>
              {!canQuery && (
                <span className="text-xs text-slate-500">
                  Your project role has read-only access.
                </span>
              )}
            </div>
          </section>

          <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-black text-slate-900">
                <History className="h-5 w-5 text-indigo-600" /> Saved answers
              </div>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                {answers.length}
              </span>
            </div>
            <div className="mt-4 max-h-[520px] space-y-2 overflow-y-auto pr-1">
              {answers.map((answer) => (
                <button
                  type="button"
                  key={answer.id}
                  onClick={() => openAnswer(answer.id)}
                  disabled={busy === `open-${answer.id}`}
                  className="w-full rounded-2xl border border-slate-200 p-3 text-left transition hover:border-indigo-300 hover:bg-indigo-50/40"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="line-clamp-2 text-sm font-bold text-slate-900">
                        {answer.question}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <StatusBadge
                          value={answer.effective_status || answer.status}
                        />
                        <span className="text-[11px] text-slate-400">
                          #{answer.id}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="mt-1 h-4 w-4 text-slate-400" />
                  </div>
                </button>
              ))}
              {!answers.length && (
                <div className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Approved answers will be reusable here when project sources
                  have not changed.
                </div>
              )}
            </div>
          </aside>
        </div>
      )}

      {step === "review" && currentAnswer && (
        <div className="space-y-5">
          {currentAnswer.is_stale && (
            <div className="flex flex-col gap-3 rounded-2xl border border-orange-200 bg-orange-50 p-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-3 text-orange-800">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <div className="font-black">
                    Project authority sources changed
                  </div>
                  <div className="text-sm">
                    This answer is visible for history but cannot be reused or
                    turned into a checklist until refreshed.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={refreshAnswer}
                disabled={busy === "refresh"}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-black text-white disabled:opacity-50"
              >
                {busy === "refresh" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Refresh answer
              </button>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-4">
            <Metric
              icon={Database}
              label="Sources used"
              value={answerSources.length}
              helper="Exact KB chunks retained"
            />
            <Metric
              icon={Layers3}
              label="Documents"
              value={
                queryMeta?.documents_used ||
                new Set(
                  answerSources.map((source) => source?.metadata?.document_id),
                ).size
              }
              helper="Approved/current authorities"
            />
            <Metric
              icon={FileCheck2}
              label="Claims"
              value={(currentAnswer.claims || []).length}
              helper="Checklist-traceable facts"
            />
            <Metric
              icon={Sparkles}
              label="Answer mode"
              value={
                queryMeta?.cache_status === "exact_cache_hit"
                  ? "Reused"
                  : "Generated"
              }
              helper={
                queryMeta?.reranking_used
                  ? "Hybrid + reranking"
                  : "Hybrid retrieval"
              }
            />
          </div>

          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50/70 p-5 md:p-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge
                      value={
                        currentAnswer.effective_status || currentAnswer.status
                      }
                    />
                    <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200">
                      Answer #{currentAnswer.id}
                    </span>
                    {currentAnswer.parent_answer_id && (
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                        Refined from #{currentAnswer.parent_answer_id}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-3 text-xl font-black text-slate-900">
                    {currentAnswer.question}
                  </h3>
                  {currentAnswer.location && (
                    <div className="mt-1 text-sm text-slate-500">
                      Area: {currentAnswer.location}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={startNewQuestion}
                    className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                  >
                    Start new question
                  </button>
                  <button
                    type="button"
                    onClick={() => setRefineOpen((value) => !value)}
                    disabled={busy === "refine"}
                    className="inline-flex items-center gap-2 rounded-xl border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-bold text-indigo-700 hover:bg-indigo-100"
                  >
                    <MessageSquareMore className="h-4 w-4" /> Refine answer
                  </button>
                </div>
              </div>
            </div>

            {refineOpen && (
              <div className="border-b border-indigo-100 bg-indigo-50/60 p-5 md:p-6">
                <div className="font-black text-indigo-950">
                  What is missing, incorrect, or outside the intended scope?
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {QUICK_REFINEMENTS.map((text) => (
                    <button
                      type="button"
                      key={text}
                      onClick={() => setRefinement(text)}
                      className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100"
                    >
                      {text}
                    </button>
                  ))}
                </div>
                <textarea
                  value={refinement}
                  onChange={(event) => setRefinement(event.target.value)}
                  rows={3}
                  className="mt-3 w-full rounded-2xl border border-indigo-200 bg-white px-4 py-3 text-sm outline-none focus:ring-4 focus:ring-indigo-100"
                  placeholder="Example: Only include field installation requirements. Exclude product accessories and procurement requirements."
                />
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={refineAnswer}
                    disabled={!refinement.trim() || busy === "refine"}
                    className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-black text-white disabled:opacity-50"
                  >
                    {busy === "refine" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    Generate revised answer
                  </button>
                  <button
                    type="button"
                    onClick={() => setRefineOpen(false)}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-slate-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="p-5 md:p-7">
              <div className="prose prose-slate max-w-none">
                {String(currentAnswer.narrative_markdown || "")
                  .split("\n")
                  .map(renderNarrativeLine)}
              </div>

              {currentAnswer.coverage_note && (
                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  <span className="font-black text-slate-800">Coverage:</span>{" "}
                  {currentAnswer.coverage_note}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                {sourceFamilies.map((family) => (
                  <span
                    key={family}
                    className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600"
                  >
                    {titleCase(family)}
                  </span>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-200 bg-slate-50 p-5 md:p-6">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="font-black text-slate-900">
                    Review the narrative as one evidence package
                  </div>
                  <div className="text-sm text-slate-500">
                    Open any [S#] citation to inspect the exact chunk before
                    approval.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canApprove &&
                    currentAnswer.status !== "approved" &&
                    !currentAnswer.is_stale && (
                      <button
                        type="button"
                        onClick={approveAnswer}
                        disabled={busy === "approve"}
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {busy === "approve" ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4" />
                        )}
                        Approve answer
                      </button>
                    )}
                  {canApprove && currentAnswer.status !== "rejected" && (
                    <button
                      type="button"
                      onClick={rejectAnswer}
                      disabled={busy === "reject"}
                      className="inline-flex items-center gap-2 rounded-xl border border-rose-300 bg-white px-4 py-2.5 text-sm font-black text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    >
                      <XCircle className="h-4 w-4" /> Reject
                    </button>
                  )}
                  {currentAnswer.status === "approved" &&
                    !currentAnswer.is_stale &&
                    canGenerate && (
                      <button
                        type="button"
                        onClick={() => setStep("checklist")}
                        className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-black text-white hover:bg-orange-600"
                      >
                        Generate checklist <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                </div>
              </div>
              {!canApprove && currentAnswer.status !== "approved" && (
                <div className="mt-3 text-xs text-slate-500">
                  A Project Manager or QA/QC Manager must approve this answer
                  before checklist generation.
                </div>
              )}
            </div>
          </section>

          {relatedAnswers.length > 0 && (
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2 font-black text-slate-900">
                <History className="h-5 w-5 text-indigo-600" /> Related approved
                answers
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {relatedAnswers.map((answer) => (
                  <button
                    type="button"
                    key={answer.id}
                    onClick={() => openAnswer(answer.id)}
                    className="rounded-2xl border border-slate-200 p-4 text-left hover:border-indigo-300 hover:bg-indigo-50/40"
                  >
                    <div className="text-sm font-bold text-slate-900">
                      {answer.question}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Approved answer #{answer.id}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {step === "checklist" && currentAnswer?.status === "approved" && (
        <div className="space-y-5">
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-7">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-100 text-orange-700">
                <ListChecks className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900">
                  Generate from approved answer #{currentAnswer.id}
                </h3>
                <p className="text-sm text-slate-500">
                  Checklist items may use only approved claims and their cited
                  source excerpts.
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-black uppercase tracking-wide text-slate-500">
                  Inspection template name / subject
                </label>
                <input
                  value={checklistScope.inspection_subject}
                  onChange={(event) =>
                    setChecklistScope((current) => ({
                      ...current,
                      inspection_subject: event.target.value,
                    }))
                  }
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wide text-slate-500">
                  Inspection stage — optional
                </label>
                <input
                  value={checklistScope.inspection_stage}
                  onChange={(event) =>
                    setChecklistScope((current) => ({
                      ...current,
                      inspection_stage: event.target.value,
                    }))
                  }
                  placeholder="Installation"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wide text-slate-500">
                  Location — optional
                </label>
                <input
                  value={checklistScope.location}
                  onChange={(event) =>
                    setChecklistScope((current) => ({
                      ...current,
                      location: event.target.value,
                    }))
                  }
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wide text-slate-500">
                  Additional instruction — optional
                </label>
                <input
                  value={checklistScope.instructions}
                  onChange={(event) =>
                    setChecklistScope((current) => ({
                      ...current,
                      instructions: event.target.value,
                    }))
                  }
                  placeholder="Group by mounting, coordination, and protection"
                  className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100"
                />
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={generateChecklist}
                disabled={busy === "checklist"}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-black text-white hover:bg-orange-600 disabled:opacity-50"
              >
                {busy === "checklist" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {preview ? "Regenerate preview" : "Generate checklist preview"}
              </button>
              <button
                type="button"
                onClick={() => setStep("review")}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700"
              >
                Back to answer
              </button>
            </div>
          </section>

          {preview && (
            <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50 p-5 md:p-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <input
                      value={preview.inspection_type_name}
                      onChange={(event) =>
                        setPreview((current) => ({
                          ...current,
                          inspection_type_name: event.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-lg font-black text-slate-900 outline-none md:min-w-[420px]"
                    />
                    <div className="mt-2 text-xs text-slate-500">
                      {preview.claim_count} claims · {preview.source_count}{" "}
                      source chunks
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={saveChecklist}
                    disabled={busy === "save-checklist"}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {busy === "save-checklist" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-4 w-4" />
                    )}
                    Save inspection template
                  </button>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {preview.items.map((item, index) => (
                  <div
                    key={`${index}-${item.claim_ids?.join("-")}`}
                    className={`p-5 md:p-6 ${item.enabled === false ? "bg-slate-50 opacity-60" : ""}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={item.enabled !== false}
                        onChange={(event) =>
                          updatePreviewItem(index, {
                            enabled: event.target.checked,
                          })
                        }
                        className="mt-2 h-4 w-4 rounded border-slate-300 text-orange-500"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="grid gap-3 md:grid-cols-[180px_minmax(0,1fr)]">
                          <label className="space-y-1">
                            <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                              Category
                            </span>
                            <input
                              value={item.item_category || ""}
                              onChange={(event) =>
                                updatePreviewItem(index, {
                                  item_category: event.target.value,
                                })
                              }
                              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-bold outline-none"
                            />
                          </label>
                          <label className="space-y-1">
                            <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                              Inspection text
                            </span>
                            <textarea
                              value={item.item_text || ""}
                              onChange={(event) =>
                                updatePreviewItem(index, {
                                  item_text: event.target.value,
                                })
                              }
                              rows={2}
                              placeholder="What the inspector must verify"
                              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-900 outline-none"
                            />
                          </label>
                        </div>
                        <label className="mt-3 block space-y-1">
                          <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                            Note / acceptance criteria
                          </span>
                          <textarea
                            value={item.inspection_note || ""}
                            onChange={(event) =>
                              updatePreviewItem(index, {
                                inspection_note: event.target.value,
                              })
                            }
                            rows={2}
                            placeholder="Acceptance condition and applicability for this inspection text"
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 outline-none"
                          />
                        </label>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {(item.claim_ids || []).map((claimId) => (
                            <span
                              key={claimId}
                              className="rounded-full bg-orange-50 px-2.5 py-1 text-[11px] font-black text-orange-700 ring-1 ring-orange-200"
                            >
                              {claimId}
                            </span>
                          ))}
                          {(item.source_ids || []).map((sourceId) => (
                            <button
                              type="button"
                              key={sourceId}
                              onClick={() =>
                                sourceById[sourceId] &&
                                setSelectedSource(sourceById[sourceId])
                              }
                              className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-black text-indigo-700 ring-1 ring-indigo-200"
                            >
                              {sourceId}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {selectedSource && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/40 backdrop-blur-sm">
          <button
            type="button"
            aria-label="Close source drawer"
            className="flex-1"
            onClick={() => setSelectedSource(null)}
          />
          <aside className="h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 p-5 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-lg bg-indigo-600 px-2 py-1 text-xs font-black text-white">
                      {selectedSource.source_id}
                    </span>
                    <StatusBadge value="approved" />
                  </div>
                  <h3 className="mt-3 text-lg font-black text-slate-900">
                    {selectedSource?.metadata?.document_name ||
                      "Project document"}
                  </h3>
                  <div className="mt-1 text-sm text-slate-500">
                    {titleCase(selectedSource?.metadata?.document_type)} · Rev{" "}
                    {selectedSource?.metadata?.revision || "—"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedSource(null)}
                  className="rounded-xl border border-slate-300 p-2 text-slate-500 hover:bg-slate-50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="space-y-5 p-5 md:p-7">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="text-xs font-bold uppercase text-slate-400">
                    Search lane
                  </div>
                  <div className="mt-1 font-bold text-slate-800">
                    {titleCase(selectedSource.search_lane)}
                  </div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="text-xs font-bold uppercase text-slate-400">
                    Relevance score
                  </div>
                  <div className="mt-1 font-bold text-slate-800">
                    {selectedSource.score != null
                      ? Number(selectedSource.score).toFixed(4)
                      : "—"}
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-black text-slate-900">
                  <Eye className="h-4 w-4 text-indigo-600" /> Exact retrieved
                  evidence
                </div>
                <pre className="mt-3 whitespace-pre-wrap rounded-2xl border border-slate-200 bg-slate-50 p-4 font-sans text-sm leading-7 text-slate-700">
                  {selectedSource.text || "No text returned."}
                </pre>
              </div>
              <details className="rounded-2xl border border-slate-200 p-4">
                <summary className="cursor-pointer text-sm font-black text-slate-800">
                  Source metadata and location
                </summary>
                <pre className="mt-3 overflow-x-auto whitespace-pre-wrap text-xs text-slate-600">
                  {JSON.stringify(
                    {
                      metadata: selectedSource.metadata,
                      location: selectedSource.location,
                      kb_search_type: selectedSource.kb_search_type,
                    },
                    null,
                    2,
                  )}
                </pre>
              </details>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default RequirementIntelligenceView;
